
import React, { useEffect, useState } from 'react';
import { NotificationManager } from 'react-notifications';
import { Quantum } from 'ldrs/react';
import 'ldrs/react/Quantum.css';
import DashboardFooter from '../components/DashboardFooter';

const AddEmployee = ({ employeeId = null }) => {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [employeesList, setEmployeesList] = useState([]);
    const [activeTab, setActiveTab] = useState('add');
    const [searchFilters, setSearchFilters] = useState({
        name: '',
        role: '',
        email: '',
    });

    const emptyForm = {
        id: null,
        name: '',
        status: true,
        address: '',
        number: '',
        emergencyMobile: '',
        dob: '',
        doj: '',
        gender: '',
        role: '',
        email: '',
         nric_fin: null,
    };

    const [formData, setFormData] = useState(emptyForm);

    const token = localStorage.getItem('token');

    /* ─── API helpers ─────────────────────────────────────────── */

    const fetchRoles = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_HOST_API_URL}/admin-user/all-roles/`, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            const data = await res.json();
            if (res.ok) setRoles(Array.isArray(data.success) ? data.success : []);
        } catch (err) {
            console.error('Role Fetch Error:', err);
            setRoles([]);
        }
    };

    const fetchAllEmployees = async () => {
        try {
            const filters = Object.fromEntries(
                Object.entries(searchFilters).filter(([, v]) => v)
            );
            const res = await fetch(`${process.env.REACT_APP_HOST_API_URL}/app/all-users/`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(filters),
            });
            if (!res.ok) throw new Error();
            const data = await res.json();
            // console.log('Employees API Response:', data);
            setEmployeesList(Array.isArray(data.data) ? data.data : []);
        } catch (err) {
            console.error('Employee list fetch error:', err);
        }
    };

    const fetchEmployee = async (id) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_HOST_API_URL}/app/all-users/`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ id }),
            });
            if (!res.ok) throw new Error();
            const emp = await res.json();
        
            setFormData({
                id,
                name: emp.name || '',
                status: emp.status ?? true,
                address: emp.address || '',
                number: emp.number || '',
                emergencyMobile: emp.emergencyMobile || '',
                dob: emp.dob || '',
                doj: emp.doj || '',
                gender: emp.gender || '',
                role: emp.role || '',
                email: emp.email || '',
                nric_fin: emp.nric_fin || '',
            });
        } catch (err) {
            console.error('Employee Fetch Error:', err);
        }
    };

    useEffect(() => {
        fetchRoles();
        fetchAllEmployees();
        if (employeeId) fetchEmployee(employeeId);
    }, []);

    /* ─── Form handlers ───────────────────────────────────────── */

    const handleChange = (e) =>
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSearchChange = (e) =>
        setSearchFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const resetForm = () => setFormData(emptyForm);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        const day = String(d.getDate()).padStart(2, '0');
        const month = d.toLocaleString('en-US', { month: 'short' });
        return `${day} ${month} ${d.getFullYear()}`;
    };

    /* ─── Submit / Update ─────────────────────────────────────── */

    const buildPayload = () => ({
        name: formData.name,
        status: formData.status ? 'Active' : 'Inactive',
        address: formData.address,
        number: formData.number,
        emergencyMobile: formData.emergencyMobile,
        dob: formatDate(formData.dob),
        doj: formatDate(formData.doj),
        gender: formData.gender,
        role: formData.role,
        email: formData.email,
        nric_fin: formData.nric_fin || '', // Include NRIC/FIN in the payload
    });

    //   console.log('Form Data:', formData);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const res = await fetch(`${process.env.REACT_APP_HOST_API_URL}/app/add-user/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(buildPayload()),
            });
            if (!res.ok) throw new Error();
            NotificationManager.success('Employee added successfully');
            resetForm();
            fetchAllEmployees();
        } catch {
            NotificationManager.error('Error adding employee');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async () => {
        if (!formData.id) return NotificationManager.error('Employee ID missing');
        try {
            setLoading(true);
            const res = await fetch(
                `${process.env.REACT_APP_HOST_API_URL}/app/add-user/`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ ...buildPayload(), id: formData.id }),
                }
            );
            if (!res.ok) throw new Error();
            NotificationManager.success('Employee updated successfully');
            resetForm();
            setActiveTab("add");
            fetchAllEmployees();
        } catch {
            NotificationManager.error('Error updating employee');
        } finally {
            setLoading(false);
        }
    };

    /* ─── Render ──────────────────────────────────────────────── */

    if (loading)
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 700 }}>
                <Quantum size="45" speed="1.75" color="blue" />
            </div>
        );

    return (
        <div className="d-flex flex-column min-vh-100 pb-2 px-3">
            <div className="flex-grow-1">
                <div className="container-fluid pb-2 px-2 px-md-4" style={{ paddingTop: 20 }}>

                    {/* ── Page header ── */}

                    <div
                        className="row mb-4 shadow-sm border-bottom sticky-top py-3 px-md-3 px-2 rounded"
                        style={{
                            zIndex: 1050,
                            background: "linear-gradient(135deg, #0564bf, #89CFF0)",
                        }}
                    >
                        <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h3 className="text-light mb-1">
                                    {activeTab === "add"
                                        ? "Add New Employee"
                                        : "Update Employee"}
                                </h3>
                                <p className="text-light mb-0">
                                    Manage employee records, roles, and contact details
                                </p>
                            </div>

                            {/* Show only in Update mode */}
                            {activeTab === "update" && (
                                <button
                                    type="button"
                                    className="btn btn-light text-primary fw-semibold"
                                    onClick={() => {
                                        resetForm();
                                        setActiveTab("add");
                                    }}
                                >
                                    <i className="fas fa-user-plus text-center"></i>
                                    Add Employee
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="row">

                        {/* ── Left: Form ── */}
                        <div className="col-12 col-lg-8 mb-4">
                            <div className="border rounded p-3 bg-white shadow-sm">
                                <h4 className="text-center mb-4" style={{ color: '#0564bf' }}>
                                    {/* {formData.id ? 'Update Employee' : 'Add Employee'} */}
                                    {activeTab === 'add' ? 'Add Employee' : 'Update Employee'}
                                </h4>

                                <form
                                    // onSubmit={handleSubmit}
                                    onSubmit={(e) => {
                                        e.preventDefault();

                                        if (activeTab === 'add') {
                                            handleSubmit(e);
                                        } else {
                                            handleUpdate();
                                        }
                                    }}
                                >
                                    <div className="row g-3">

                                        <div className="col-12 col-md-6">
                                            <input
                                                type="text"
                                                name="name"
                                                placeholder="Full Name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="form-control"
                                                required
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <input
                                                type="email"
                                                name="email"
                                                placeholder="Email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className="form-control"
                                                required
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <input
                                                type="text"
                                                name="number"
                                                placeholder="Mobile Number"
                                                value={formData.number}
                                                onChange={handleChange}
                                                className="form-control"
                                                required
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <input
                                                type="text"
                                                name="emergencyMobile"
                                                placeholder="Emergency Mobile"
                                                value={formData.emergencyMobile}
                                                onChange={handleChange}
                                                className="form-control"
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <label className="form-label text-muted small mb-1">Date of Birth</label>
                                            <input
                                                type="date"
                                                name="dob"
                                                value={formData.dob}
                                                onChange={handleChange}
                                                className="form-control"
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <label className="form-label text-muted small mb-1">Date of Joining</label>
                                            <input
                                                type="date"
                                                name="doj"
                                                value={formData.doj}
                                                onChange={handleChange}
                                                className="form-control"
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <select
                                                name="gender"
                                                value={formData.gender}
                                                onChange={handleChange}
                                                className="form-control"
                                            >
                                                <option value="">Select Gender</option>
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <select
                                                name="role"
                                                value={formData.role}
                                                onChange={handleChange}
                                                className="form-control"
                                                required
                                            >
                                                <option value="">Select Role</option>
                                                {roles.map((r, i) => (
                                                    <option key={i} value={r}>{r}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <select
                                                name="status"
                                                className="form-control"
                                                value={String(formData.status)}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({ ...prev, status: e.target.value === 'true' }))
                                                }
                                            >
                                                <option value="true">Active</option>
                                                <option value="false">Inactive</option>
                                            </select>
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <input
                                                type="text"
                                                name="nric_fin"
                                                placeholder="NRIC/FIN"
                                                value={formData.nric_fin || ''}
                                                onChange={handleChange}
                                                className="form-control"
                                            />
                                        </div>

                                        <div className="col-12">
                                            <textarea
                                                name="address"
                                                placeholder="Address"
                                                value={formData.address}
                                                onChange={handleChange}
                                                className="form-control"
                                                rows={3}
                                            />
                                        </div>
                                    </div>


                                    <div className="d-flex justify-content-center gap-3 mt-4 flex-wrap">
                                        {activeTab === 'add' ? (
                                            <button
                                                type="submit"
                                                className="btn btn-outline-primary px-4"
                                                disabled={loading}
                                            >
                                                Save Employee
                                            </button>
                                        ) : (
                                            <>
                                                <button
                                                    type="submit"
                                                    className="btn btn-outline-primary px-4"
                                                    disabled={loading || !formData.id}
                                                >
                                                    Update Employee
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary px-4"
                                                    onClick={resetForm}
                                                >
                                                    Clear
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* ── Right: Employee list ── */}
                        <div className={`col-12 ${activeTab === 'update' ? 'col-lg-4' : 'col-lg-4'} mb-4`}>
                            <div className="border rounded p-3 bg-white shadow-sm d-flex flex-column">
                                <h4 className="text-center mb-4" style={{ color: '#0564bf' }}>
                                    All Employees
                                </h4>

                                {/* Search */}
                                <div className="mb-3">
                                    <div className="row g-2">
                                        <div className="col-12">
                                            <input
                                                type="text"
                                                name="name"
                                                placeholder="Search by name..."
                                                value={searchFilters.name}
                                                onChange={handleSearchChange}
                                                className="form-control form-control-sm"
                                            />
                                        </div>
                                        <div className="col-6">
                                            <input
                                                type="text"
                                                name="role"
                                                placeholder="Role..."
                                                value={searchFilters.role}
                                                onChange={handleSearchChange}
                                                className="form-control form-control-sm"
                                            />
                                        </div>
                                        <div className="col-6">
                                            <input
                                                type="text"
                                                name="email"
                                                placeholder="Email..."
                                                value={searchFilters.email}
                                                onChange={handleSearchChange}
                                                className="form-control form-control-sm"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-sm btn-primary mt-2 w-100"
                                        onClick={fetchAllEmployees}
                                    >
                                        <i className="fas fa-search me-1"></i> Search
                                    </button>
                                </div>

                                {/* Cards */}
                                <div className="flex-grow-1 overflow-auto" style={{ maxHeight: '100vh' }}>
                                    {employeesList.map((emp) => (
                                        <div
                                            key={emp.id}
                                            className="mb-3 p-3 border rounded"
                                            style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }}
                                            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 2px 12px rgba(5,100,191,0.15)')}
                                            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
                                            onClick={() => {
                                                setActiveTab("update");
                                                const toInputDate = (dateStr) => {
                                                    if (!dateStr) return '';
                                                    const d = new Date(dateStr);
                                                    if (isNaN(d)) return '';
                                                    return d.toISOString().split('T')[0];
                                                };

                                                setFormData({
                                                    id: emp.id,
                                                    name: emp.full_name || '',
                                                    status: emp.is_active ?? true,
                                                    address: emp.address || '',
                                                    number: String(emp.mobile || ''),
                                                    emergencyMobile: String(emp.emergency_mobile || ''),
                                                    dob: toInputDate(emp.birth_date),
                                                    doj: toInputDate(emp.joining_date),
                                                    gender: emp.gender || '',
                                                    role: emp.role || '',
                                                    email: emp.email || '',
                                                    nric_fin: emp.nric_fin || '',

                                                });
                                            }}
                                        >
                                            {/* Avatar row */}
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div className="d-flex align-items-center">
                                                    <div
                                                        className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                                        style={{
                                                            width: 40,
                                                            height: 40,
                                                            backgroundColor:
                                                                emp.gender === 'Female'
                                                                    ? '#e91e63'
                                                                    : emp.gender === 'Other'
                                                                        ? '#6c757d'
                                                                        : '#0564bf',
                                                            color: 'white',
                                                            fontSize: 14,
                                                            fontWeight: 'bold',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {emp.full_name?.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0 fw-bold" style={{ color: '#0564bf' }}>
                                                            {emp.full_name}
                                                        </h6>
                                                        <small className="text-muted">
                                                            {emp.role || emp.username} • {emp.username}
                                                        </small>
                                                    </div>
                                                </div>
                                                <span className={`badge ${emp.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                    {emp.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>

                                            {/* Details */}
                                            <div className="row g-1 mb-2">
                                                {emp.email && (
                                                    <div className="col-12">
                                                        <div className="d-flex align-items-center">
                                                            <i className="fas fa-envelope me-2 text-muted" style={{ fontSize: 12 }}></i>
                                                            <small className="text-truncate">{emp.email}</small>
                                                        </div>
                                                    </div>
                                                )}
                                                {emp.number && (
                                                    <div className="col-12">
                                                        <div className="d-flex align-items-center">
                                                            <i className="fas fa-phone me-2 text-muted" style={{ fontSize: 12 }}></i>
                                                            <small>{emp.number}</small>
                                                        </div>
                                                    </div>
                                                )}
                                                {emp.doj && (
                                                    <div className="col-12">
                                                        <div className="d-flex align-items-center">
                                                            <i className="fas fa-calendar-alt me-2 text-muted" style={{ fontSize: 12 }}></i>
                                                            <small>Joined: {emp.doj}</small>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Footer */}
                                            <div className="d-flex justify-content-between align-items-center">
                                                <small className="text-muted">
                                                    {emp.gender}
                                                </small>
                                            </div>
                                        </div>
                                    ))}

                                    {employeesList.length === 0 && (
                                        <div className="text-center py-4 text-muted">
                                            <i className="fas fa-users" style={{ fontSize: 48 }}></i>
                                            <p className="mt-2">No employees found</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>

                </div>
            </div>
            <DashboardFooter />
        </div>
    );
};

export default AddEmployee;